import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { usePostHog } from "posthog-js/react";
import { useFeatureFlags } from "../../../utils/featureFlags";
import { generateRandomId, permissionName, servicesName, setLenderCase } from "../../../components/Utils/helperFunction";
import { ERRORS_MESSAGE, PERMISSION_TAB, PermissionAccessOptions, SUCCESS_MESSAGE, tabList } from "../../../constants";
import { getRoleByUserId, saveUserRolePermissions, saveValue } from "../../../store/slices/userSlice";
import { openSnackbar } from "../../../store/slices/snackbarSlice";

export const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3, width: "100%" }}>{children}</Box>}
    </div>
  );
};

export function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

export const Permission = ({ permissionInfo, setPermissionInfo }) => {
  const posthog = usePostHog();
  const dispatch = useDispatch();
  const { userId } = useParams();
  const [tab, setTab] = useState(0);
  const navigate = useNavigate();
  const { isCommissionEnabled } = useFeatureFlags();
  const [applicationAccess, setApplicationAccess] = useState("self");
  const [userType, setUserType] = useState("");
  const canViewCommission = isCommissionEnabled();
  const filteredTabList = canViewCommission ? tabList : tabList.filter((tab) => tab !== PERMISSION_TAB.COMMISSION);

  const [applicationAccesses, setApplicationAccesses] = useState({
    create: true,
    edit: true,
    submit: true,
    status_change: true,
    internal_submit: false,
    pre_supplier_data: false,
    quote_view: false,
    quote_create: false,
    quote_edit: false,
    quote_convert: false,
    transfer_ownership: false,
    followers_edit: false,
  });

  const [userSettingsAccess, setUserSettingsAccess] = useState(false);
  const [userAccess, setUserAccess] = useState("own");
  const [userAccesses, setUserAccesses] = useState({
    invite: true,
    remove: true,
    edit_role: true,
    status_change: true,
    permission_edit: true,
  });

  const [servicesAccess, setServicesAccess] = useState({
    All: true,
    bank_statements: true,
    asic_extract: true,
    asic_extract_ppsr: true,
    id_verification: true,
    blug_flag: true,
    blug_flag_ppsr: true,
    equifax_basic_report: true,
    equifax_commercial_report: true,
    equifax_with_onescore_report: true,
    equifax_scored_commercial_report: true,
    commercial_credit_score: true,
    commercial_credit_report: true,
    employment_income_verification: true,
  });
  const [organisationAccess, setOrganisationAccess] = useState({
    edit: true,
    create: true,
    view: true,
    permission_edit: true,
    brand_edit: true,
  });

  const [commissionAccess, setCommissionAccess] = useState({
    report_view: true,
  });

  const [templatesAccess, setTemplatesAccess] = useState({
    sms_edit: true,
    sms_create: true,
    sms_view: true,
    sms_remove: true,
    email_edit: true,
    email_create: true,
    email_view: true,
    email_remove: true,
    document_edit: true,
    document_create: true,
    document_view: true,
    document_remove: true,
  });

  const all = "All";
  const allLenders = "All";
  const [lendersAccess, setLendersAccess] = useState({
    All: true,
  });

  useEffect(() => {
    if (userId) {
      getUserPermission(userId);
    }
  }, [userId]);

  useEffect(() => {
    if (permissionInfo) {
      setOrganisationAccess(permissionInfo?.organisationAccess);
      setCommissionAccess(permissionInfo?.commissionAccess);
      setLendersAccess(permissionInfo?.lendersAccess);
      setServicesAccess(permissionInfo?.servicesAccess);
      setUserAccesses(permissionInfo?.userAccesses);
      setUserAccess(permissionInfo?.userAccess);
      setApplicationAccesses(permissionInfo?.applicationAccesses);
      setApplicationAccess(permissionInfo?.applicationAccess);
      setUserSettingsAccess(permissionInfo?.userSettingsAccess);
      setTemplatesAccess(permissionInfo?.templatesAccess);
    }
  }, [permissionInfo]);

  const getUserPermission = async (userId) => {
    await dispatch(getRoleByUserId({ _id: userId })).then((res) => {
      if (res?.payload?.status_code === 200) {
        const { payload } = res;
        const updatedServicesAccess = { ...servicesAccess, ...payload?.data?.services };
        const isAllChecked = Object.values(payload?.data?.services).every((value) => value);
        updatedServicesAccess["All"] = isAllChecked;

        const updatedLendersAccess = { ...lendersAccess, ...payload?.data?.lenders };
        const isAllLendersChecked = payload?.data?.lenders
          ? Object.values(payload?.data?.lenders).every((value) => value)
          : false;
        updatedLendersAccess["All"] = isAllLendersChecked;
        const { access, ...withoutAccessApplicationAccess } = payload?.data?.application;
        const newUserAccessList = Object.fromEntries(
          Object.entries(payload?.data?.user).filter(([key]) => key !== "access"),
        );
        setApplicationAccesses(withoutAccessApplicationAccess);
        setUserType(payload?.data?.userType);
        setApplicationAccess(payload?.data?.application?.access);
        setUserAccesses(newUserAccessList);
        setUserSettingsAccess(payload?.data?.billing?.all || false);
        setUserAccess(payload?.data?.user?.access);
        setServicesAccess(updatedServicesAccess);
        setLendersAccess(updatedLendersAccess);
        setOrganisationAccess(payload?.data?.org);
        setCommissionAccess(payload?.data?.commission || { report_view: false });
        setTemplatesAccess(payload?.data?.templates);
      }
    });
  };

  const handle = {
    applicationAccess: (event) => {
      const { name, value, checked } = event.target;
      if (name === "access") {
        setApplicationAccess(value);
        if (permissionInfo) setPermissionInfo({ ...permissionInfo, applicationAccess: value });
        return;
      } else {
        setApplicationAccesses({ ...applicationAccesses, [name]: checked });
        if (permissionInfo)
          setPermissionInfo({ ...permissionInfo, applicationAccesses: { ...applicationAccesses, [name]: checked } });
        return;
      }
    },
    organisationAccess: (event) => {
      const { name, checked } = event.target;
      const updatedOrganisationAccess = { ...organisationAccess, [name]: checked };
      setOrganisationAccess(updatedOrganisationAccess);
      if (permissionInfo) setPermissionInfo({ ...permissionInfo, organisationAccess: updatedOrganisationAccess });
    },
    commissionAccess: (event) => {
      const { name, checked } = event.target;
      const updatedCommissionAccess = { ...commissionAccess, [name]: checked };
      setCommissionAccess(updatedCommissionAccess);
      if (permissionInfo) setPermissionInfo({ ...permissionInfo, commissionAccess: updatedCommissionAccess });
    },
    templatesAccess: (event) => {
      const { name, checked } = event.target;
      const updatedTemplatesAccess = { ...templatesAccess, [name]: checked };
      setTemplatesAccess(updatedTemplatesAccess);
      if (permissionInfo) setPermissionInfo({ ...permissionInfo, templatesAccess: updatedTemplatesAccess });
    },
    lendersAccess: (event) => {
      const { checked, name } = event.target;
      if (name === allLenders) {
        const allCheckedLenders = Object.fromEntries(
          Object.entries(lendersAccess).map(([key, value]) => [key, checked]),
        );
        setLendersAccess(allCheckedLenders);
        if (permissionInfo) setPermissionInfo({ ...permissionInfo, lendersAccess: allCheckedLenders });
      } else {
        const updatedLendersAccess = { ...lendersAccess, [name]: checked };
        const { All, ...UpdateLenders } = { ...updatedLendersAccess };
        const isAllChecked = Object.values(UpdateLenders).every((value) => value);
        updatedLendersAccess[allLenders] = isAllChecked;
        updatedLendersAccess[allLenders?.toLowerCase()] = isAllChecked;
        setLendersAccess(updatedLendersAccess);
        if (permissionInfo) setPermissionInfo({ ...permissionInfo, lendersAccess: updatedLendersAccess });
      }
    },
    servicesAccess: (event) => {
      const { checked, name } = event.target;
      if (name === all) {
        const allCheckedService = Object.fromEntries(
          Object.entries(servicesAccess).map(([key, value]) => [key, checked]),
        );
        setServicesAccess(allCheckedService);
        if (permissionInfo) setPermissionInfo({ ...permissionInfo, servicesAccess: allCheckedService });
      } else {
        const updatedServicesAccess = { ...servicesAccess, [name]: checked };
        const { All, ...UpdateServices } = { ...updatedServicesAccess };
        const isAllChecked = Object.values(UpdateServices).every((value) => value);
        updatedServicesAccess[all] = isAllChecked;
        setServicesAccess(updatedServicesAccess);
        if (permissionInfo) setPermissionInfo({ ...permissionInfo, servicesAccess: updatedServicesAccess });
      }
    },
    // userSettingsAccess: (event) => {
    //   const { checked } = event.target;
    //   setUserSettingsAccess(checked);
    // },
    userAccess: (event) => {
      const { name, value, checked } = event.target;
      if (name === "access") {
        setUserAccess(value);
        if (permissionInfo) setPermissionInfo({ ...permissionInfo, userAccess: value });
        return;
      } else {
        setUserAccesses({ ...userAccesses, [name]: checked });
        if (permissionInfo)
          setPermissionInfo({ ...permissionInfo, userAccesses: { ...userAccesses, [name]: checked } });
        return;
      }
    },
    saveUserPermission: () => {
      const { All, ...UpdateServicesAccess } = { ...servicesAccess };
      const { AllLenders, ...UpdateLendersAccess } = { ...lendersAccess };
      const allApplicationAccess = {
        ...applicationAccesses,
        access: applicationAccess,
      };
      const allUserAccess = { ...userAccesses, access: userAccess };

      const data = {
        data: {
          permission: {
            application: allApplicationAccess,
            billing: { all: userSettingsAccess },
            services: UpdateServicesAccess,
            lenders: UpdateLendersAccess,
            user: allUserAccess,
            org: organisationAccess,
            commission: commissionAccess,
            templates: templatesAccess,
          },
          userType: userType,
        },
        id: userId,
      };
      dispatch(saveUserRolePermissions(data)).then(({ payload, error }) => {
        if (error) {
          dispatch(
            openSnackbar({
              message: payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          posthog?.capture("user_role_permissions_saved", {
            updatedUserId: userId,
            userType: data.data.userType,
            permission: data.data.permission,
          });
          dispatch(
            openSnackbar({
              message: payload?.data?.status_message || SUCCESS_MESSAGE.fetchMsg,
              intent: "success",
            }),
          );
          // Need to set page and limit value for prevent user list page crash
          dispatch(saveValue({ page: 1, limit: 10 }))
          // Defer navigation to avoid Data Grid on /users mounting before refs are ready
          navigate("/users")
        }
      });
    },
  };

  const groupTemplatesAccess = (templatesAccess = {}) => {
    return Object.entries(templatesAccess).reduce((acc, [key, value]) => {
      const [category, action] = key.split("_"); // sms_edit → sms, edit

      if (!acc[category]) {
        acc[category] = {};
      }

      acc[category][action] = value;
      return acc;
    }, {});
  };

  const groupedTemplatesAccess = groupTemplatesAccess(templatesAccess);

  const permissionTabs = (tab) => {
    switch (tab) {
      case PERMISSION_TAB.APPLICATIONS:
        return (
          <FormControl>
            <Stack direction={"row"} columnGap={1} justifyContent={"center"} alignItems={"center"}>
              <Typography fontSize={"1rem"} color={"rgba(0, 0, 0, 0.6)"}>
                <FormLabel>{permissionName("view")}</FormLabel>
              </Typography>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                size="small"
                fullWidth
                name="access"
                value={applicationAccess || ""}
                onChange={handle?.applicationAccess}
              >
                {PermissionAccessOptions.map((item) => (
                  <MenuItem value={item.value} key={item.value}>
                    {_.startCase(item.label)}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            {Object.entries(applicationAccesses)?.map(([key, value], index) => (
              <Stack key={key} justifyContent={"space-between"} alignItems={"center"} flexDirection={"row"}>
                <FormLabel>{permissionName(key)}</FormLabel>
                <Switch name={key} checked={value} onChange={handle?.applicationAccess} />
              </Stack>
            ))}
          </FormControl>
        );

      case PERMISSION_TAB.USERS:
        return (
          <FormControl>
            <Stack direction={"row"} columnGap={1} justifyContent={"center"} alignItems={"center"}>
              <Typography fontSize={"1rem"} color={"rgba(0, 0, 0, 0.6)"}>
                <FormLabel>{permissionName("view")}</FormLabel>
              </Typography>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                size="small"
                fullWidth
                name="access"
                value={userAccess || ""}
                onChange={handle?.userAccess}
              >
                {PermissionAccessOptions.map((item) => (
                  <MenuItem value={item.value} key={item.value}>
                    {_.startCase(item.label)}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            {Object.entries(userAccesses)?.map(([key, value]) => (
              <Stack key={key} justifyContent={"space-between"} alignItems={"center"} flexDirection={"row"}>
                <FormLabel>{permissionName(key)}</FormLabel>
                <Switch name={key} checked={value} onChange={handle?.userAccess} />
              </Stack>
            ))}
          </FormControl>
        );

      case PERMISSION_TAB.SERVICES:
        return (
          <FormControl>
            {Object.entries(servicesAccess)?.map(([key, value]) => (
              <Stack key={key} justifyContent={"space-between"} alignItems={"center"} flexDirection={"row"}>
                <FormLabel>{servicesName(key)}</FormLabel>
                <Switch name={key} checked={value} onChange={handle?.servicesAccess} />
              </Stack>
            ))}
          </FormControl>
        );

      case PERMISSION_TAB.ORGANISATIONS:
        return (
          <FormControl>
            {Object.entries(organisationAccess)?.map(([key, value]) => (
              <Stack key={key} justifyContent={"space-between"} alignItems={"center"} flexDirection={"row"}>
                <FormLabel>{permissionName(key)}</FormLabel>
                <Switch name={key} checked={value} onChange={handle?.organisationAccess} />
              </Stack>
            ))}
          </FormControl>
        );

      case PERMISSION_TAB.TEMPLATES:
        return (
          <FormControl>
            {groupedTemplatesAccess &&
              Object.entries(groupedTemplatesAccess)?.map(([category, permissions]) => (
                <Box key={`${generateRandomId(5)}`} sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      textTransform: "capitalize",
                      mb: 1.5,
                      color: "rgba(0, 0, 0, 0.87)",
                    }}
                  >
                    {category}
                  </Typography>
                  {Object.entries(permissions)?.map(([action, value]) => {
                    const disabledActions = ["create", "remove"]
                    if (disabledActions.includes(action)) {
                      return (<></>);
                    }
                    return (
                      <Stack
                        key={`${category}_${action}`}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        flexDirection={"row"}
                        sx={{ mb: 1 }}
                      >
                        <FormLabel>{permissionName(action)}</FormLabel>
                        <Switch name={`${category}_${action}`} checked={value} onChange={handle?.templatesAccess} />
                      </Stack>
                    )
                  })}
                </Box>
              )
              )}
          </FormControl>
        );

      case PERMISSION_TAB.LENDERS:
        return (
          <FormControl>
            {Object.entries(lendersAccess)?.sort()?.filter(([key]) => key !== allLenders?.toLowerCase())?.map(([key, value]) => (
              <Stack key={key} justifyContent={"space-between"} alignItems={"center"} flexDirection={"row"}>
                <FormLabel>{setLenderCase(key)}</FormLabel>
                <Switch name={key} checked={value} onChange={handle?.lendersAccess} />
              </Stack>
            ))}
          </FormControl>
        );

      case PERMISSION_TAB.COMMISSION:
        return (
          <FormControl>
            {Object.entries(commissionAccess)?.map(([key, value]) => (
              <Stack key={key} justifyContent={"space-between"} alignItems={"center"} flexDirection={"row"}>
                <FormLabel>{permissionName(key)}</FormLabel>
                <Switch name={key} checked={value} onChange={handle?.commissionAccess} />
              </Stack>
            ))}
          </FormControl>
        );
      default:
        return;
    }
  };

  return (
    <Stack sx={{ width: "100%" }}>
      {userId && (
        <Stack flexDirection={"row"}>
          <Typography
            variant="h1"
            style={{
              fontSize: "24px",
              fontWeight: 800,
              letterSpacing: "-0.5px",
            }}
            mb={"10px"}
          >
            User Role & Permissions
          </Typography>
          <Button sx={{ marginLeft: "80px" }} variant="outlined" onClick={handle.saveUserPermission}>
            Save
          </Button>
        </Stack>
      )}
      <Stack flexDirection={"row"}>
        <Tabs orientation="vertical" onChange={(event, newValue) => setTab(newValue)} value={tab}>
          {filteredTabList.map((tabInfo, i) => (
            <Tab label={tabInfo} key={tabInfo} {...a11yProps(i)} />
          ))}
        </Tabs>
        {filteredTabList.map((tabInfo, i) => (
          <TabPanel value={tab} index={i}>
            {permissionTabs(tabInfo)}
          </TabPanel>
        ))}
      </Stack>
    </Stack>
  );
};
